import React from 'react';
import { AlertTriangle, Droplets, Mountain, Flame, ArrowRight, CheckCircle2 } from 'lucide-react';

export const ActionCenter = ({ health, onNavigate }) => {
    // Safety check in case health hasn't loaded yet
    const alerts = health?.alerts || [];

    // 1. HAPPY PATH: No alerts means the user is in "Flow"
    if (alerts.length === 0) {
        return (
            <div className="bg-gradient-to-r from-emerald-900/40 to-cyan-900/40 border border-emerald-500/30 p-6 rounded-2xl mb-8 flex items-center justify-between animate-fade-in">
                <div>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <CheckCircle2 className="w-6 h-6 text-emerald-400" /> 
                        Systems Nominal
                    </h3>
                    <p className="text-slate-300 text-sm mt-1">
                        Fires extinguished. Water flowing. Earth stable. You are riding the wave.
                    </p>
                </div>
            </div>
        );
    }

    // 2. ALERT PATH: Sort alerts so 'critical' is always first, then grab the top priority
    const priorityAlert = alerts.sort((a, b) => {
        if (a.level === 'critical' && b.level !== 'critical') return -1;
        if (b.level === 'critical' && a.level !== 'critical') return 1;
        return 0;
    })[0];

    // Helper: Get Icon based on Element
    const getIcon = (element) => {
        switch(element) {
            case 'fire': return <Flame className="w-8 h-8 text-orange-500 animate-pulse" />;
            case 'water': return <Droplets className="w-8 h-8 text-blue-400 animate-bounce" />;
            case 'earth': return <Mountain className="w-8 h-8 text-emerald-400" />;
            default: return <AlertTriangle className="w-8 h-8 text-slate-400" />;
        }
    };

    // Helper: Get Styles based on Element and Severity
    const getStyles = (element, level) => {
        if (level === 'critical') return "bg-red-950/50 border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.2)]";
        
        switch(element) {
            case 'fire': return "bg-orange-900/20 border-orange-500/30";
            case 'water': return "bg-blue-900/20 border-blue-500/30";
            case 'earth': return "bg-emerald-900/20 border-emerald-500/30";
            default: return "bg-slate-800 border-slate-700";
        }
    };

    return (
        <div className={`p-6 rounded-2xl border mb-8 flex flex-col md:flex-row items-center gap-6 transition-all duration-500 animate-fade-in ${getStyles(priorityAlert.element, priorityAlert.level)}`}>
            <div className="p-3 bg-slate-950/50 rounded-full border border-white/10 shrink-0">
                {getIcon(priorityAlert.element)}
            </div>
            
            <div className="flex-1 text-center md:text-left">
                <h3 className="text-[10px] font-bold text-white uppercase tracking-widest mb-1 opacity-70">
                    {priorityAlert.level === 'critical' ? 'Critical Action Required' : 'Suggested Action'}
                </h3>
                <p className="text-xl font-bold text-white">
                    {priorityAlert.message}
                </p>
            </div>

            <button 
                onClick={() => onNavigate(priorityAlert.link)}
                className="group flex items-center gap-2 bg-white text-slate-900 px-6 py-3 rounded-xl font-bold hover:scale-105 transition-transform whitespace-nowrap shadow-lg shrink-0"
            >
                Take Action <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
        </div>
    );
};